import React, { useState, useEffect, useCallback } from 'react';
import ApiClient, { Submission, SubmissionStatus, Form, SubmissionsResponse, IPSubmissionsData, ASNSubmissionsData } from '../api/client';
import SubmissionDetail from './SubmissionDetail';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { usePermissions } from '../context/PermissionContext';
import { DataTableShell, PageHeader } from './ui';

interface SubmissionManagerProps {
  apiClient: ApiClient;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

// URL param helpers
const getUrlParams = () => new URLSearchParams(window.location.search);

const updateUrlParams = (params: Record<string, string | number | undefined>) => {
  const url = new URL(window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== 0) {
      url.searchParams.set(key, String(value));
    } else {
      url.searchParams.delete(key);
    }
  });
  window.history.replaceState({}, '', url.toString());
};

const STATUS_COLORS: Record<SubmissionStatus, { bg: string; color: string }> = {
  pending: { bg: '#e0e7ff', color: '#3730a3' },
  approved: { bg: '#d4edda', color: '#155724' },
  flagged: { bg: '#fff3cd', color: '#856404' },
  spam: { bg: '#f8d7da', color: '#721c24' },
};

type SortField = 'id' | 'status' | 'spamScore' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const SubmissionManager: React.FC<SubmissionManagerProps> = ({ apiClient }) => {
  const { hasPermission } = usePermissions();

  // Initialize state from URL params
  const initialParams = getUrlParams();

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Permissions
  const canModerate = hasPermission('submissions.moderate');
  const canDelete = hasPermission('submissions.delete');

  // Filters (from URL)
  const [statusFilter, setStatusFilter] = useState<SubmissionStatus | ''>(
    (initialParams.get('status') as SubmissionStatus) || ''
  );
  const [formFilter, setFormFilter] = useState<string>(
    initialParams.get('form') || ''
  );

  // IP/ASN drill-down filters
  const [ipFilter, setIpFilter] = useState<number | null>(
    initialParams.get('ipId') ? Number(initialParams.get('ipId')) : null
  );
  const [asnFilter, setAsnFilter] = useState<number | null>(
    initialParams.get('asn') ? Number(initialParams.get('asn')) : null
  );
  const [ipInfo, setIpInfo] = useState<IPSubmissionsData['ip'] | null>(null);
  const [asnInfo, setAsnInfo] = useState<ASNSubmissionsData['asn'] | null>(null);

  // Sorting (from URL)
  const [sortField, setSortField] = useState<SortField>(
    (initialParams.get('sortBy') as SortField) || 'createdAt'
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    (initialParams.get('sortDir') as SortDirection) || 'desc'
  );

  // Pagination (from URL)
  const [pageSize, setPageSize] = useState<number>(
    Number(initialParams.get('limit')) || DEFAULT_PAGE_SIZE
  );
  const [offset, setOffset] = useState(
    Number(initialParams.get('offset')) || 0
  );
  const [total, setTotal] = useState(0);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<'approve' | 'flag' | 'spam' | 'delete' | null>(null);

  // Export
  const [exporting, setExporting] = useState(false);

  // Pending count
  const [pendingCount, setPendingCount] = useState(0);

  const loadSubmissions = useCallback(async (reset = false) => {
    setLoading(true);
    setError(null);
    try {
      const newOffset = reset ? 0 : offset;

      // Use drill-down endpoints if IP or ASN filter is set
      if (ipFilter) {
        const data = await apiClient.getIPSubmissions(ipFilter, pageSize);
        setIpInfo(data.ip);
        // Map IP submissions to standard Submission format
        const subs = Array.isArray(data.submissions) ? data.submissions : [];
        const mapped = subs.map(s => ({
          ...s,
          submittingIp: data.ip.ipAddress,
        })) as unknown as Submission[];
        setSubmissions(mapped);
        setTotal(subs.length);
        if (reset) {
          setOffset(0);
          setSelectedIds(new Set());
        }
        return;
      }

      if (asnFilter) {
        const data = await apiClient.getASNSubmissions(asnFilter, pageSize);
        setAsnInfo(data.asn);
        // Map ASN submissions to standard Submission format
        const subs = Array.isArray(data.submissions) ? data.submissions : [];
        const mapped = subs as unknown as Submission[];
        setSubmissions(mapped);
        setTotal(subs.length);
        if (reset) {
          setOffset(0);
          setSelectedIds(new Set());
        }
        return;
      }

      // Standard submissions list
      const params: Record<string, any> = {
        limit: pageSize,
        page: Math.floor(newOffset / pageSize) + 1,
        sortBy: sortField,
        sortDirection,
      };
      if (statusFilter) params.status = statusFilter;
      if (formFilter) params.formHash = formFilter;

      const response = await apiClient.listSubmissions(params);

      setSubmissions(response.submissions);
      setTotal(response.meta.total || 0);
      setIpInfo(null);
      setAsnInfo(null);
      if (reset) {
        setOffset(0);
        setSelectedIds(new Set());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [apiClient, offset, pageSize, statusFilter, formFilter, sortField, sortDirection, ipFilter, asnFilter]);

  const loadForms = useCallback(async () => {
    try {
      const data = await apiClient.listForms();
      setForms(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load forms for filter:', err);
    }
  }, [apiClient]);

  const loadPendingCount = useCallback(async () => {
    try {
      const response = await apiClient.listSubmissions({ status: 'pending', limit: 1 });
      setPendingCount(response.meta.total || 0);
    } catch (err) {
      console.error('Failed to load pending count:', err);
    }
  }, [apiClient]);

  useEffect(() => {
    loadForms();
    loadPendingCount();
  }, [loadForms, loadPendingCount]);

  // Sync state to URL params
  useEffect(() => {
    updateUrlParams({
      status: statusFilter || undefined,
      form: formFilter || undefined,
      sortBy: sortField !== 'createdAt' ? sortField : undefined,
      sortDir: sortDirection !== 'desc' ? sortDirection : undefined,
      limit: pageSize !== DEFAULT_PAGE_SIZE ? pageSize : undefined,
      offset: offset || undefined,
      ipId: ipFilter || undefined,
      asn: asnFilter || undefined,
    });
  }, [statusFilter, formFilter, sortField, sortDirection, pageSize, offset, ipFilter, asnFilter]);

  // Reload when filters or sort changes (reset to page 1)
  useEffect(() => {
    loadSubmissions(true);
  }, [statusFilter, formFilter, sortField, sortDirection, pageSize, ipFilter, asnFilter]);

  // Reload when offset changes (pagination)
  useEffect(() => {
    if (offset > 0) {
      loadSubmissions(false);
    }
  }, [offset]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortHeader: React.FC<{ field: SortField; children: React.ReactNode }> = ({ field, children }) => (
    <th>
      <button className="table-sort-button" onClick={() => handleSort(field)}>
        {children} {sortField === field ? (sortDirection === 'asc' ? 'up' : 'down') : ''}
      </button>
    </th>
  );

  const handleStatusChange = async (id: number, action: 'approve' | 'flag' | 'spam') => {
    try {
      let updated: Submission;
      switch (action) {
        case 'approve':
          updated = await apiClient.approveSubmission(id);
          break;
        case 'flag':
          updated = await apiClient.flagSubmission(id);
          break;
        case 'spam':
          updated = await apiClient.markSubmissionSpam(id);
          break;
      }
      setSubmissions(submissions.map(s => s.id === id ? { ...s, ...updated } : s));
      if (selectedSubmission?.id === id) {
        setSelectedSubmission({ ...selectedSubmission, ...updated });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update submission');
    }
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    try {
      await apiClient.deleteSubmission(deletingId);
      setSubmissions(submissions.filter(s => s.id !== deletingId));
      if (selectedSubmission?.id === deletingId) {
        setSelectedSubmission(null);
      }
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(deletingId);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete submission');
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewDetail = async (submission: Submission) => {
    try {
      const detail = await apiClient.getSubmission(submission.id);
      setSelectedSubmission(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submission details');
    }
  };

  // Bulk actions
  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;

    const ids = Array.from(selectedIds);
    try {
      switch (bulkAction) {
        case 'approve': {
          const result = await apiClient.bulkApproveSubmissions(ids);
          setSuccess(`Approved ${result.approved} submissions`);
          break;
        }
        case 'flag': {
          const result = await apiClient.bulkFlagSubmissions(ids);
          setSuccess(`Flagged ${result.flagged} submissions`);
          break;
        }
        case 'spam': {
          const result = await apiClient.bulkMarkSubmissionsSpam(ids);
          setSuccess(`Marked ${result.markedSpam} submissions as spam`);
          break;
        }
        case 'delete': {
          const result = await apiClient.bulkDeleteSubmissions(ids);
          setSuccess(`Deleted ${result.deleted} submissions`);
          break;
        }
      }
      setSelectedIds(new Set());
      setBulkAction(null);
      await loadSubmissions(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform bulk action');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === submissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submissions.map(s => s.id)));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Export
  const handleExport = async (format: 'csv' | 'json') => {
    setExporting(true);
    try {
      const blob = await apiClient.exportSubmissions({
        format,
        formHash: formFilter || undefined,
        status: statusFilter || undefined,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submissions-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccess(`Exported submissions as ${format.toUpperCase()}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export submissions');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getFormName = (hash: string) => {
    const form = forms.find(f => f.hash === hash);
    return form?.name || hash.substring(0, 8) + '...';
  };

  return (
    <div className="manager-page submissions-page">
      {error && (
        <div className="alert alert-error">
          <span>⚠️</span>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✓</span>
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}

      <PageHeader
        title="Submissions"
        actions={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => handleExport('csv')}
              disabled={exporting}
              title="Export as CSV"
            >
              CSV
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleExport('json')}
              disabled={exporting}
              title="Export as JSON"
            >
              JSON
            </button>
            <button className="btn btn-primary" onClick={() => { loadSubmissions(true); loadPendingCount(); }}>
              Refresh
            </button>
          </>
        }
      />

      {pendingCount > 0 && (
        <div className="submissions-status-line">
          {pendingCount > 0 && (
            <span className="status-pill status-pill-processing">
              <span />
              {pendingCount} processing
            </span>
          )}
        </div>
      )}

      <div className="submissions-shell">
        {/* IP/ASN Filter Banner */}
        {(ipInfo || asnInfo) && (
          <div style={{
            background: '#e0e7ff',
            border: '1px solid #c7d2fe',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div>
              {ipInfo && (
                <span>
                  Showing submissions from IP <strong style={{ fontFamily: 'monospace' }}>{ipInfo.ipAddress}</strong>
                  {ipInfo.listType !== 'neutral' && (
                    <span style={{
                      marginLeft: '8px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: ipInfo.listType === 'whitelist' ? '#d4edda' : '#f8d7da',
                      color: ipInfo.listType === 'whitelist' ? '#155724' : '#721c24',
                    }}>
                      {ipInfo.listType}
                    </span>
                  )}
                </span>
              )}
              {asnInfo && (
                <span>
                  Showing submissions from ASN <strong>AS{asnInfo.asn}</strong>
                  {asnInfo.name && <span> ({asnInfo.name})</span>}
                  {asnInfo.shadowbanned && (
                    <span style={{
                      marginLeft: '8px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: '#343a40',
                      color: '#fff',
                    }}>
                      BANNED
                    </span>
                  )}
                </span>
              )}
            </div>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => {
                setIpFilter(null);
                setAsnFilter(null);
                setIpInfo(null);
                setAsnInfo(null);
              }}
            >
              ✕ Clear Filter
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="submissions-filters">
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, fontSize: '13px' }}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SubmissionStatus | '')}
              style={{ width: '100%' }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="flagged">Flagged</option>
              <option value="spam">Spam</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, fontSize: '13px' }}>
              Form
            </label>
            <select
              value={formFilter}
              onChange={(e) => setFormFilter(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">All Forms</option>
              {(Array.isArray(forms) ? forms : []).map(form => (
                <option key={form.hash} value={form.hash}>{form.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (canModerate || canDelete) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            background: '#e3f2fd',
            borderRadius: '6px',
            marginBottom: '16px',
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 500 }}>{selectedIds.size} selected</span>
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              {canModerate && (
                <>
                  <button
                    className="btn btn-sm"
                    style={{ background: '#28a745', color: 'white' }}
                    onClick={() => setBulkAction('approve')}
                  >
                    ✓ Approve
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ background: '#ffc107', color: '#333' }}
                    onClick={() => setBulkAction('flag')}
                  >
                    ⚑ Flag
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ background: '#dc3545', color: 'white' }}
                    onClick={() => setBulkAction('spam')}
                  >
                    ✕ Spam
                  </button>
                </>
              )}
              {canDelete && (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => setBulkAction('delete')}
                >
                  🗑️ Delete
                </button>
              )}
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Submissions Table - Scrollable */}
        {loading && submissions.length === 0 ? (
          <div className="empty-state" style={{ flex: 1 }}>
            <div className="loading-spinner"></div>
            <p>Loading submissions...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="empty-state" style={{ flex: 1 }}>
            <div className="empty-state-icon">📭</div>
            <h3>No submissions found</h3>
            <p>No submissions match the current filters</p>
          </div>
        ) : (
          <DataTableShell>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === submissions.length && submissions.length > 0}
                      onChange={toggleSelectAll}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                  </th>
                  <SortHeader field="id">ID</SortHeader>
                  <th>Form</th>
                  <SortHeader field="status">Status</SortHeader>
                  <SortHeader field="spamScore">Spam Score</SortHeader>
                  <th>Origin</th>
                  <SortHeader field="createdAt">Date</SortHeader>
                  <th className="actions-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(submission => (
                  <tr
                    key={submission.id}
                    style={{
                      cursor: 'pointer',
                      background: selectedIds.has(submission.id) ? 'var(--teal-soft)' : undefined,
                    }}
                    onClick={() => handleViewDetail(submission)}
                  >
                    <td onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(submission.id)}
                        onChange={() => toggleSelect(submission.id)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                    </td>
                    <td className="data-id">#{submission.id}</td>
                    <td className="data-title">{getFormName(submission.formHash)}</td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 700,
                        backgroundColor: STATUS_COLORS[submission.status]?.bg || '#f0f0f0',
                        color: STATUS_COLORS[submission.status]?.color || '#333',
                      }}>
                        {submission.status}
                      </span>
                      {submission.testMode && (
                        <span style={{
                          display: 'inline-block',
                          marginLeft: '6px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600,
                          backgroundColor: '#e0e7ff',
                          color: '#3730a3',
                        }}>
                          TEST
                        </span>
                      )}
                    </td>
                    <td>
                      <span style={{
                        color: submission.spamScore > 0.7 ? '#dc3545' :
                               submission.spamScore > 0.4 ? '#856404' : '#155724'
                      }}>
                        {(submission.spamScore * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="data-muted">
                      {submission.origin || '-'}
                    </td>
                    <td className="data-muted">
                      {formatDate(submission.createdAt)}
                    </td>
                    <td className="actions-cell" onClick={e => e.stopPropagation()}>
                      <div className="row-actions">
                        {canModerate && (
                          <>
                            {submission.status !== 'approved' && (
                              <button
                                className="btn btn-sm"
                                style={{ background: '#28a745', color: 'white' }}
                                onClick={() => handleStatusChange(submission.id, 'approve')}
                                title="Approve"
                              >
                                ✓
                              </button>
                            )}
                            {submission.status !== 'flagged' && (
                              <button
                                className="btn btn-sm"
                                style={{ background: '#ffc107', color: '#333' }}
                                onClick={() => handleStatusChange(submission.id, 'flag')}
                                title="Flag"
                              >
                                ⚑
                              </button>
                            )}
                            {submission.status !== 'spam' && (
                              <button
                                className="btn btn-sm"
                                style={{ background: '#dc3545', color: 'white' }}
                                onClick={() => handleStatusChange(submission.id, 'spam')}
                                title="Mark as Spam"
                              >
                                ✕
                              </button>
                            )}
                          </>
                        )}
                        {canDelete && (
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setDeletingId(submission.id)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableShell>
        )}

        {/* Pagination - Always at bottom */}
        {total > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '16px',
            borderTop: '1px solid var(--border)',
            flexShrink: 0,
          }}>
            <button
              className="btn btn-secondary"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - pageSize))}
            >
              ← Previous
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                Showing {offset + 1}-{Math.min(offset + pageSize, total)} of {total.toLocaleString()}
              </span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                style={{ padding: '4px 8px', fontSize: '13px' }}
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-secondary"
              disabled={offset + pageSize >= total || loading}
              onClick={() => setOffset(offset + pageSize)}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (() => {
        const currentIndex = submissions.findIndex(s => s.id === selectedSubmission.id);
        const globalIndex = offset + currentIndex;
        const hasPrev = globalIndex > 0;
        const hasNext = globalIndex < total - 1;

        const handlePrev = async () => {
          if (currentIndex > 0) {
            // Move within current page - fetch full detail
            const detail = await apiClient.getSubmission(submissions[currentIndex - 1].id);
            setSelectedSubmission(detail);
          } else if (offset > 0) {
            // Fetch previous page and select last item
            const newOffset = Math.max(0, offset - pageSize);
            const params: Record<string, any> = {
              limit: pageSize,
              page: Math.floor(newOffset / pageSize) + 1,
              sortBy: sortField,
              sortDirection,
            };
            if (statusFilter) params.status = statusFilter;
            if (formFilter) params.formHash = formFilter;

            const response = await apiClient.listSubmissions(params);
            setSubmissions(response.submissions);
            setOffset(newOffset);
            // Fetch full detail for the last item
            const detail = await apiClient.getSubmission(response.submissions[response.submissions.length - 1].id);
            setSelectedSubmission(detail);
          }
        };

        const handleNext = async () => {
          if (currentIndex < submissions.length - 1) {
            // Move within current page - fetch full detail
            const detail = await apiClient.getSubmission(submissions[currentIndex + 1].id);
            setSelectedSubmission(detail);
          } else if (offset + pageSize < total) {
            // Fetch next page and select first item
            const newOffset = offset + pageSize;
            const params: Record<string, any> = {
              limit: pageSize,
              page: Math.floor(newOffset / pageSize) + 1,
              sortBy: sortField,
              sortDirection,
            };
            if (statusFilter) params.status = statusFilter;
            if (formFilter) params.formHash = formFilter;

            const response = await apiClient.listSubmissions(params);
            setSubmissions(response.submissions);
            setOffset(newOffset);
            // Fetch full detail for the first item
            const detail = await apiClient.getSubmission(response.submissions[0].id);
            setSelectedSubmission(detail);
          }
        };

        return (
          <SubmissionDetail
            submission={selectedSubmission}
            formName={getFormName(selectedSubmission.formHash)}
            onClose={() => setSelectedSubmission(null)}
            onStatusChange={canModerate ? (action: 'approve' | 'flag' | 'spam') => handleStatusChange(selectedSubmission.id, action) : undefined}
            onDelete={canDelete ? () => setDeletingId(selectedSubmission.id) : undefined}
            onPrev={handlePrev}
            onNext={handleNext}
            hasPrev={hasPrev}
            hasNext={hasNext}
          />
        );
      })()}

      {/* Delete Confirmation */}
      {deletingId !== null && (
        <ConfirmDeleteModal
          title="Delete Submission"
          message="Delete this submission? This action cannot be undone."
          confirmButtonLabel="🗑️ Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeletingId(null)}
        />
      )}

      {/* Bulk Action Confirmation */}
      {bulkAction !== null && (
        <ConfirmDeleteModal
          title={`Bulk ${bulkAction.charAt(0).toUpperCase() + bulkAction.slice(1)}`}
          message={`${bulkAction === 'delete' ? 'Delete' : bulkAction.charAt(0).toUpperCase() + bulkAction.slice(1)} ${selectedIds.size} submissions?`}
          confirmButtonLabel={
            bulkAction === 'approve' ? '✓ Approve All' :
            bulkAction === 'flag' ? '⚑ Flag All' :
            bulkAction === 'spam' ? '✕ Mark All as Spam' :
            '🗑️ Delete All'
          }
          onConfirm={handleBulkAction}
          onCancel={() => setBulkAction(null)}
        />
      )}
    </div>
  );
};

export default SubmissionManager;
